<template>
  <v-dialog v-model="dialog" fullscreen hide-overlay transition="dialog-bottom-transition">
    <v-card dark class="d-flex flex-column fill-height">
      <!-- Toolbar -->
      <v-toolbar dark color="#1E1E1E" elevation="1">
        <v-btn icon dark @click="$emit('close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-toolbar-title class="text-subtitle-1 font-weight-bold text-truncate" style="max-width: 300px">
          {{ document.title || "Dokument" }}
        </v-toolbar-title>

        <v-spacer></v-spacer>

        <!-- Quick Actions -->
        <v-btn
          v-if="userService.hasRight('d_fetchAllDocs') && document.approvalStatus === 'notSigned' && document.currentUserStatus !== 'accepted'"
          color="success"
          class="mr-2"
          depressed
          small
          @click="signDocument">
          <v-icon left small>mdi-pen</v-icon>
          Visieren
        </v-btn>

        <v-menu offset-y left>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon v-bind="attrs" v-on="on">
              <v-icon>mdi-dots-vertical</v-icon>
            </v-btn>
          </template>
          <v-list dense>
            <v-list-item @click="$emit('download-document', document)">
              <v-list-item-icon class="mr-3"><v-icon>mdi-download</v-icon></v-list-item-icon>
              <v-list-item-title>Herunterladen</v-list-item-title>
            </v-list-item>
            <v-list-item @click="$emit('edit-document', document)">
              <v-list-item-icon class="mr-3"><v-icon>mdi-pencil</v-icon></v-list-item-icon>
              <v-list-item-title>Bearbeiten</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-toolbar>

      <!-- Main Content Grid -->
      <v-card-text class="pa-0 fill-height" :style="$vuetify.breakpoint.mdAndUp ? 'overflow: hidden' : 'overflow-y: auto'">
        <v-row class="fill-height ma-0" no-gutters>
          <!-- Left Column: Preview -->
          <v-col cols="12" md="7" class="d-flex flex-column" :class="{ 'fill-height': $vuetify.breakpoint.mdAndUp }" style="background-color: #000">
            <div
              v-if="!isApp"
              class="relative-position"
              :class="{ 'fill-height': $vuetify.breakpoint.mdAndUp }"
              :style="$vuetify.breakpoint.mdAndUp ? 'overflow: hidden' : 'height: 50vh; overflow: hidden;'">
              <div v-if="previewBlobUrl" class="fill-height w-100">
                <iframe :src="previewBlobUrl" style="width: 100%; height: 100%; border: none"></iframe>
              </div>
            </div>

            <div v-else class="d-flex align-center justify-center pa-4" :class="{ 'fill-height': $vuetify.breakpoint.mdAndUp }">
              <v-alert type="info" text> Vorschau in der App nicht möglich. Bitte herunterladen. </v-alert>
            </div>
          </v-col>

          <!-- Right Column: Details & Tabs -->
          <v-col
            cols="12"
            md="5"
            class="d-flex flex-column"
            :class="{ 'fill-height': $vuetify.breakpoint.mdAndUp }"
            style="background-color: #272727; border-left: 1px solid #444">
            <v-tabs v-model="localActiveTab" background-color="#1E1E1E" dark grow slider-color="primary">
              <v-tab><v-icon small class="mr-1">mdi-information-outline</v-icon> Details</v-tab>
              <v-tab><v-icon small class="mr-1">mdi-comment-outline</v-icon> Chat</v-tab>
              <v-tab><v-icon small class="mr-1">mdi-history</v-icon> Verlauf</v-tab>
              <v-tab><v-icon small class="mr-1">mdi-account-group-outline</v-icon> User</v-tab>
            </v-tabs>

            <v-tabs-items v-model="localActiveTab" dark class="fill-height scroll-y transparent pa-0">
              <!-- DETAILS TAB -->
              <v-tab-item class="fill-height px-5">
                <div class="pa-4">
                  <v-alert :type="alertTypeByStatus(document)" text dense class="mb-4">
                    <span class="d-flex justify-space-between align-center">
                      {{ alertTextByStatus(document) }}
                      <v-btn v-if="document.approvalStatus === 'notSigned' && document.currentUserStatus !== 'accepted'" @click="activeTab = 1" text>
                        jetzt visieren</v-btn
                      >
                    </span>
                  </v-alert>

                  <!-- General Info -->
                  <v-card flat color="transparent" class="mb-4">
                    <v-subheader class="pl-0 text-uppercase font-weight-bold grey--text text--lighten-1">Allgemein</v-subheader>
                    <div class="text-body-2">
                      <v-row dense>
                        <v-col cols="4" class="grey--text">Titel</v-col>
                        <v-col cols="8">{{ document.title || "-" }}</v-col>
                      </v-row>
                      <v-row dense class="mt-2">
                        <v-col cols="4" class="grey--text">Beschreibung</v-col>
                        <v-col cols="8">{{ document.description || "-" }}</v-col>
                      </v-row>
                    </div>
                  </v-card>

                  <v-divider class="grey darken-3 my-3"></v-divider>

                  <!-- Invoice Details -->
                  <v-card flat color="transparent" class="mb-4">
                    <v-subheader class="pl-0 text-uppercase font-weight-bold grey--text text--lighten-1">Rechnung</v-subheader>
                    <div class="text-body-2">
                      <v-row dense>
                        <v-col cols="4" class="grey--text">Datum</v-col>
                        <v-col cols="8">{{ formatDate(document.date) }}</v-col>
                      </v-row>
                      <v-row dense class="mt-2">
                        <v-col cols="4" class="grey--text">Rechnungssteller</v-col>
                        <v-col cols="8">{{ document.invoiceIssuer ? document.invoiceIssuer.title : "-" }}</v-col>
                      </v-row>
                      <v-row dense class="mt-2">
                        <v-col cols="4" class="grey--text">Rechnungs Nr.</v-col>
                        <v-col cols="8">{{ document.invoiceNumber || "-" }}</v-col>
                      </v-row>
                      <v-row dense class="mt-2">
                        <v-col cols="4" class="grey--text">Betrag</v-col>
                        <v-col cols="8" class="font-weight-bold">{{ document.amount ? document.amount : "-" }}</v-col>
                      </v-row>
                    </div>
                  </v-card>

                  <v-divider class="grey darken-3 my-3"></v-divider>

                  <!-- Mail Info -->
                  <v-card flat color="transparent">
                    <v-subheader class="pl-0 text-uppercase font-weight-bold grey--text text--lighten-1">E-Mail</v-subheader>
                    <div v-if="mailDetails" class="text-body-2">
                      <v-row dense>
                        <v-col cols="4" class="grey--text">Datum</v-col>
                        <v-col cols="8">{{ formatDateTime(mailDetails.date) }}</v-col>
                      </v-row>
                      <v-row dense class="mt-2">
                        <v-col cols="4" class="grey--text">Betreff</v-col>
                        <v-col cols="8">{{ mailDetails.subject }}</v-col>
                      </v-row>
                      <v-row dense class="mt-2">
                        <v-col cols="4" class="grey--text">Von</v-col>
                        <v-col cols="8">{{ mailDetails.from.join(", ") }}</v-col>
                      </v-row>
                      <v-row dense class="mt-2">
                        <v-col cols="12" class="grey--text">Inhalt</v-col>
                        <v-col cols="12" class="pt-1 grey--text text--lighten-2" style="white-space: pre-wrap">{{
                          mailDetails.text || "Kein Text"
                        }}</v-col>
                      </v-row>
                    </div>
                    <div v-else class="text-caption grey--text">Keine Mail-Daten verknüpft.</div>
                  </v-card>
                </div>
              </v-tab-item>

              <!-- COMMENTS TAB -->
              <v-tab-item class="fill-height">
                <div class="d-flex flex-column fill-height">
                  <div class="flex-grow-1 scroll-y pa-4">
                    <div v-if="document.comments && document.comments.length > 0">
                      <v-card v-for="(comment, index) in document.comments" :key="index" flat color="grey darken-3" class="mb-3 rounded-lg pa-3">
                        <div class="d-flex justify-space-between mb-1">
                          <span class="font-weight-bold caption text-uppercase grey--text text--lighten-2">
                            {{ comment.user.vorname }} {{ comment.user.nachname }}
                          </span>
                          <span class="caption grey--text">{{ formatDateTime(comment.createdAt) }}</span>
                        </div>
                        <div class="body-2 white--text">{{ comment.comment }}</div>
                        <div class="mt-2" v-if="comment.status">
                          <v-chip x-small :color="comment.status === 'accepted' ? 'green' : 'red'" outlined>
                            {{ comment.status === "accepted" ? "Akzeptiert" : "Abgelehnt" }}
                          </v-chip>
                        </div>
                      </v-card>
                    </div>
                    <div v-else class="text-center grey--text mt-10">Noch keine Kommentare.</div>
                  </div>

                  <div class="pa-3 grey darken-4 border-top">
                    <v-textarea
                      v-model="newComment"
                      placeholder="Kommentar schreiben..."
                      rows="2"
                      dense
                      outlined
                      hide-details
                      class="mb-2"></v-textarea>
                    <div class="d-flex justify-end">
                      <v-btn text small color="error" @click="decideAssignment('rejected')" class="mr-2">Ablehnen</v-btn>
                      <v-btn depressed small color="primary" @click="decideAssignment('accepted')">Akzeptieren / Senden</v-btn>
                    </div>
                  </div>
                </div>
              </v-tab-item>

              <!-- HISTORY TAB -->
              <v-tab-item class="fill-height">
                <div class="pa-4 scroll-y fill-height">
                  <v-timeline align-top dense v-if="document.accessHistory && document.accessHistory.length > 0">
                    <v-timeline-item v-for="(entry, index) in document.accessHistory" :key="index" small color="primary">
                      <div class="py-1">
                        <div class="caption grey--text">{{ formatDateTime(entry.grantedAt) }}</div>
                        <div class="font-weight-bold body-2">
                          {{ entry.user.vorname ? entry.user.vorname + " " + entry.user.nachname : entry.user }}
                        </div>
                        <div class="caption">
                          {{ entry.action === "removed" ? "Zugriff entzogen von" : "Zugriff erhalten von" }}
                          {{ entry.grantedBy.vorname }} {{ entry.grantedBy.nachname }}
                        </div>
                      </div>
                    </v-timeline-item>
                  </v-timeline>
                  <div v-else class="text-center grey--text mt-5">Kein Verlauf vorhanden.</div>
                </div>
              </v-tab-item>

              <!-- USERS TAB -->
              <v-tab-item class="fill-height">
                <div class="pa-4 scroll-y fill-height">
                  <v-card outlined color="transparent" class="mb-4 pa-3">
                    <div class="text-subtitle-2 mb-2">Benutzer hinzufügen</div>
                    <user-search
                      :value="newAssignedUser"
                      ignoreRights
                      instructor
                      :disabled="false"
                      :key="newAssignedUser"
                      label="Suchen..."
                      @update="updateNewAssignedUser"></user-search>
                    <v-btn block color="primary" depressed small class="mt-2" :disabled="!newAssignedUser" @click="addAssignedUser">
                      Hinzufügen
                    </v-btn>
                  </v-card>

                  <div class="text-subtitle-2 mb-2 grey--text">Zugewiesene Benutzer</div>
                  <v-list dense color="transparent">
                    <v-list-item v-for="(assignment, index) in document.assignedUsers" :key="index" class="px-0">
                      <v-list-item-avatar color="grey darken-3" size="32">
                        <span class="ma-auto white--text caption">{{
                          (assignment.user.vorname || "U").charAt(0) + (assignment.user.nachname || "").charAt(0)
                        }}</span>
                      </v-list-item-avatar>
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ assignment.user.vorname ? assignment.user.vorname + " " + assignment.user.nachname : assignment.user }}
                        </v-list-item-title>
                        <v-list-item-subtitle class="caption">Rechte: {{ assignment.rights }}</v-list-item-subtitle>
                      </v-list-item-content>
                      <v-list-item-action>
                        <v-btn icon small color="red lighten-2" @click="removeAssignedUser(index)">
                          <v-icon small>mdi-delete</v-icon>
                        </v-btn>
                      </v-list-item-action>
                    </v-list-item>
                  </v-list>
                  <v-alert v-if="!document.assignedUsers || document.assignedUsers.length === 0" type="info" text dense>
                    Keine Benutzer zugewiesen
                  </v-alert>
                </div>
              </v-tab-item>
            </v-tabs-items>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import userSearch from "@/components/userSearch.vue"
import userService from "@/services/userService"

export default {
  name: "DocumentActionsDialog",
  components: { userSearch },
  props: {
    dialog: {
      type: Boolean,
      required: true,
    },
    document: {
      type: Object,
      required: true,
    },
    previewBlobUrl: {
      type: String,
      default: null,
    },
    previewHtml: {
      type: String,
      default: null,
    },
    // Removed previewHeight, isResizing props as we use CSS grid now
    mailDetails: {
      type: Object,
      default: null,
    },
    activeTab: {
      type: Number,
      default: 0,
    },
    newComment: {
      type: String,
      default: "",
    },
    newAssignedUser: {
      type: [String, Object],
      default: null,
    },
    newAssignedUserRights: {
      type: String,
      default: "manage",
    },
  },
  data() {
    return {
      localActiveTab: this.activeTab,
      userService,
    }
  },
  watch: {
    activeTab(newVal) {
      this.localActiveTab = newVal
    },
    localActiveTab(newVal) {
      this.$emit("update:activeTab", newVal)
    },
  },
  computed: {
    isApp() {
      return window.flutter_inappwebview || false
    },
  },
  methods: {
    alertTextByStatus(doc) {
      if (doc.approvalStatus === "signed") return "Dokument ist visiert"
      if (doc.approvalStatus === "declined") return "Dokument wurde abgelehnt"
      // NEU: Wenn du schon visiert hast, das Dokument aber noch auf andere wartet
      if (doc.currentUserStatus === "accepted") return "Von dir visiert (wartet auf andere)"
      if (doc.approvalStatus === "notSigned") return "Dokument ist noch nicht visiert"
      return "Unbekannter Status"
    },
    alertTypeByStatus(doc) {
      if (doc.approvalStatus === "signed") return "success"
      if (doc.approvalStatus === "declined") return "error"
      // NEU: Für dich ist es ein Erfolg, also machen wir die Box grün
      if (doc.currentUserStatus === "accepted") return "success"
      if (doc.approvalStatus === "notSigned") return "info"
      return "info"
    },
    formatDate(date) {
      return date ? this.$moment(date).format("DD.MM.YYYY") : "-"
    },
    formatDateTime(date) {
      return date ? this.$moment(date).format("DD.MM.YY HH:mm") : "-"
    },
    signDocument() {
      if (confirm("Dokument visieren?")) {
        // Check if user is assigned, if not, request auto-assignment
        const payload = { status: "accepted", comment: "" }
        if (!this.document.assignedUsers || this.document.assignedUsers.length === 0) {
          const currentUser = this.userService.getUser()
          if (currentUser && currentUser._id) {
            payload.assignUser = currentUser._id
          }
        }
        this.$emit("decide-assignment", payload)
      }
    },
    decideAssignment(status) {
      if (status === "rejected" && (!this.newComment || this.newComment.length === 0)) {
        this.$toast.error("Gib bitte eine Begründung an")
        return
      }
      this.$emit("decide-assignment", { status, comment: this.newComment })
      // Note: newComment is cleared in parent
    },
    removeAssignedUser(index) {
      this.$emit("remove-assigned-user", index)
    },
    addAssignedUser() {
      this.$emit("add-assigned-user")
    },
    updateNewAssignedUser(value) {
      this.$emit("update-new-assigned-user", value)
    },
  },
}
</script>

<style scoped>
::v-deep .v-slide-group__prev {
  display: none !important;
}
.scroll-y {
  overflow-y: auto;
}
.relative-position {
  position: relative;
}
.border-right {
  border-right: 1px solid #444;
}
.border-top {
  border-top: 1px solid #444;
}
/* Hide scrollbar for Chrome, Safari and Opera */
.preview-container::-webkit-scrollbar {
  display: none;
}

/* Hide scrollbar for IE, Edge and Firefox */
.preview-container {
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
}
</style>
